import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  AgencyFormField,
  AgencyFormFieldErrors,
  AgencyFormFields,
} from '@/modules/agencies/types/agencyForm'
import type { AgencyStatusAction } from '@/modules/agencies/utils/agencyStatusAction'

type StatusConfirmState = {
  action: AgencyStatusAction
  agencyId: string
  agencyName: string
} | null

type AgenciesUiState = {
  isCreateOpen: boolean
  isEditOpen: boolean
  editingAgencyId: string | null
  fields: AgencyFormFields
  fieldErrors: AgencyFormFieldErrors
  submitError: string | null
  isSubmitting: boolean
  slugTouched: boolean
  statusConfirm: StatusConfirmState
  statusPending: boolean
  statusError: string | null
}

const emptyFields: AgencyFormFields = {
  name: '',
  slug: '',
}

const initialState: AgenciesUiState = {
  isCreateOpen: false,
  isEditOpen: false,
  editingAgencyId: null,
  fields: emptyFields,
  fieldErrors: {},
  submitError: null,
  isSubmitting: false,
  slugTouched: false,
  statusConfirm: null,
  statusPending: false,
  statusError: null,
}

function resetForm(state: AgenciesUiState) {
  state.fields = emptyFields
  state.fieldErrors = {}
  state.submitError = null
  state.isSubmitting = false
  state.slugTouched = false
}

const agenciesUiSlice = createSlice({
  name: 'agenciesUi',
  initialState,
  reducers: {
    createAgencyOpened(state) {
      state.isCreateOpen = true
      state.isEditOpen = false
      state.editingAgencyId = null
      state.statusConfirm = null
      state.statusError = null
      resetForm(state)
    },
    createAgencyClosed(state) {
      state.isCreateOpen = false
      resetForm(state)
    },
    editAgencyOpened(
      state,
      action: PayloadAction<{ agencyId: string; name: string; slug: string }>,
    ) {
      state.isEditOpen = true
      state.isCreateOpen = false
      state.editingAgencyId = action.payload.agencyId
      state.statusConfirm = null
      state.statusError = null
      state.fields = {
        name: action.payload.name,
        slug: action.payload.slug,
      }
      state.fieldErrors = {}
      state.submitError = null
      state.isSubmitting = false
      // Match create: keep slug synced from name until the user edits slug.
      state.slugTouched = false
    },
    editAgencyClosed(state) {
      state.isEditOpen = false
      state.editingAgencyId = null
      resetForm(state)
    },
    agencyFormFieldChanged(
      state,
      action: PayloadAction<{ field: AgencyFormField; value: string }>,
    ) {
      const { field, value } = action.payload
      state.fields[field] = value
      state.submitError = null

      if (field === 'slug') {
        state.slugTouched = true
      }

      if (state.fieldErrors[field]) {
        delete state.fieldErrors[field]
      }
    },
    agencyFormSlugSynced(state, action: PayloadAction<string>) {
      if (!state.slugTouched) {
        state.fields.slug = action.payload
        if (state.fieldErrors.slug) {
          delete state.fieldErrors.slug
        }
      }
    },
    agencyFormValidationFailed(
      state,
      action: PayloadAction<AgencyFormFieldErrors>,
    ) {
      state.fieldErrors = action.payload
      state.isSubmitting = false
    },
    agencyFormSubmitted(state) {
      state.isSubmitting = true
      state.submitError = null
      state.fieldErrors = {}
    },
    agencyFormSucceeded(state) {
      state.isCreateOpen = false
      state.isEditOpen = false
      state.editingAgencyId = null
      resetForm(state)
    },
    agencyFormFailed(state, action: PayloadAction<string>) {
      state.isSubmitting = false
      state.submitError = action.payload
    },
    statusConfirmOpened(
      state,
      action: PayloadAction<{
        action: AgencyStatusAction
        agencyId: string
        agencyName: string
      }>,
    ) {
      state.statusConfirm = action.payload
      state.statusPending = false
      state.statusError = null
      state.isCreateOpen = false
      state.isEditOpen = false
    },
    statusConfirmClosed(state) {
      if (state.statusPending) {
        return
      }
      state.statusConfirm = null
      state.statusError = null
      state.statusPending = false
    },
    statusActionSubmitted(state) {
      state.statusPending = true
      state.statusError = null
    },
    statusActionSucceeded(state) {
      state.statusConfirm = null
      state.statusPending = false
      state.statusError = null
    },
    statusActionFailed(state, action: PayloadAction<string>) {
      state.statusPending = false
      state.statusError = action.payload
    },
  },
})

export const {
  createAgencyOpened,
  createAgencyClosed,
  editAgencyOpened,
  editAgencyClosed,
  agencyFormFieldChanged,
  agencyFormSlugSynced,
  agencyFormValidationFailed,
  agencyFormSubmitted,
  agencyFormSucceeded,
  agencyFormFailed,
  statusConfirmOpened,
  statusConfirmClosed,
  statusActionSubmitted,
  statusActionSucceeded,
  statusActionFailed,
} = agenciesUiSlice.actions

export const agenciesUiReducer = agenciesUiSlice.reducer
